import { MutableState, State } from '../State';
import { getJSTDateTime, getJSTDaysDiff } from '../utils/time';
import features from '../ruleset/features';
import { PetStatusNotification } from './petChatbot.ruleset';
import { sendAnalyticsEvents } from '../analytics';
import { ScheduledActionAPI } from '../config/scheduledActions/createScheduledActions';
import { SyncActionAPI } from '../config/actions/createActions';

/**
 * Status message ramp segments based on days since install.
 */
export type StatusMessageSegment = 'A' | 'B' | 'C';

/**
 * Half-day period for limited message tracking.
 */
export type HalfDayPeriod = 'morning' | 'afternoon';

/**
 * Half-day period time ranges in JST hours [startHour, endHour).
 * End hour 0 means midnight (next day).
 */
export const halfDayPeriods: Record<HalfDayPeriod, readonly [number, number]> =
  {
    morning: [0, 15], // midnight - 3 PM
    afternoon: [15, 0], // 3 PM - midnight
  };

/**
 * Configuration for a status message ramp segment.
 */
export interface StatusMessageRampConfig {
  maxMessagesPerDay: number;
  maxLimitedPerHalfDay: number;
  maxLimitedPerType: number;
  alwaysSend: PetStatusNotification[];
  limited: PetStatusNotification[];
  suppress: PetStatusNotification[];
}

/**
 * Ramp configurations for each segment.
 *
 * Segment A (D0-D1): Max 4 messages/day, 1 limited/half-day, 1 per type/day
 * Segment B (D2-D6): Max 5 messages/day, 2 limited/half-day, 1 per type/day
 * Segment C (D7+): Max 7 messages/day, 2 limited/half-day, 2 per type/day
 */
export const statusMessageRampConfigs: Record<
  StatusMessageSegment,
  StatusMessageRampConfig
> = {
  A: {
    maxMessagesPerDay: 4,
    maxLimitedPerHalfDay: 1,
    maxLimitedPerType: 1,
    alwaysSend: ['sick', 'dying', 'pee', 'hungry', 'want_to_go_out'],
    limited: ['evolving', 'tooth_decay', 'lonely', 'dirty', 'scared', 'dead'],
    suppress: ['very_hungry', 'very_lonely', 'very_dirty', 'very_pee'],
  },
  B: {
    maxMessagesPerDay: 5,
    maxLimitedPerHalfDay: 2,
    maxLimitedPerType: 1,
    alwaysSend: ['sick', 'dying', 'dead', 'pee', 'hungry', 'want_to_go_out'],
    limited: ['tooth_decay', 'lonely', 'dirty', 'evolving', 'scared'],
    suppress: ['very_hungry', 'very_lonely', 'very_dirty', 'very_pee'],
  },
  C: {
    maxMessagesPerDay: 7,
    maxLimitedPerHalfDay: 2,
    maxLimitedPerType: 2,
    alwaysSend: ['sick', 'dying', 'dead', 'hungry', 'pee', 'want_to_go_out'],
    limited: [
      'lonely',
      'tooth_decay',
      'dirty',
      'evolving',
      'scared',
      'very_hungry',
    ],
    suppress: ['very_lonely', 'very_pee', 'very_dirty'],
  },
};

/**
 * Get the start of the current JST day as a timestamp.
 * Used to detect day change for counter reset.
 */
export function getJSTDayNumber(now: number): number {
  return getJSTDateTime(now).startOf('day').toMillis();
}

/**
 * Get the current half-day period based on JST hour.
 */
export function getJSTHalfDayPeriod(now: number): HalfDayPeriod {
  const jstHour = getJSTDateTime(now).hour;
  const [, morningEnd] = halfDayPeriods.morning;
  return jstHour < morningEnd ? 'morning' : 'afternoon';
}

/**
 * Get a unique identifier for the current half-day period.
 * Returns the timestamp of the start of the current period.
 */
export function getJSTHalfDayNumber(now: number): number {
  const jst = getJSTDateTime(now);
  const hour = jst.hour;
  const [morningStart, morningEnd] = halfDayPeriods.morning;
  const [afternoonStart] = halfDayPeriods.afternoon;

  if (hour < morningEnd) {
    return jst
      .set({ hour: morningStart, minute: 0, second: 0, millisecond: 0 })
      .toMillis();
  }
  return jst
    .set({ hour: afternoonStart, minute: 0, second: 0, millisecond: 0 })
    .toMillis();
}

/**
 * Calculate days since install for a user based on JST calendar days.
 */
export function getDaysSinceInstall(createdAt: number, now: number): number {
  return Math.max(0, getJSTDaysDiff(now, createdAt));
}

/**
 * Get the segment based on days since install.
 */
export function getStatusMessageSegment(
  daysSinceInstall: number,
): StatusMessageSegment {
  if (daysSinceInstall <= 1) return 'A';
  if (daysSinceInstall <= 6) return 'B';
  return 'C';
}

/**
 * Result of checking if a status message should be sent under ramp rules.
 */
export type RampFilterResult = {
  shouldSend: boolean;
  reason:
    | 'control_bucket'
    | 'always_send'
    | 'limited_allowed'
    | 'suppressed'
    | 'daily_cap_reached'
    | 'half_day_cap_reached'
    | 'per_type_cap_reached';
  incrementTotal: boolean;
  incrementLimited: boolean;
  status: PetStatusNotification;
};

/**
 * Current ramp context with all calculated values.
 * Single source of truth for counter state calculations.
 */
export interface RampContext {
  projectDaysSinceInstall: number;
  segment: StatusMessageSegment;
  config: StatusMessageRampConfig;
  currentJstDay: number;
  currentHalfDayNumber: number;
  isNewDay: boolean;
  isNewHalfDay: boolean;
  totalCount: number;
  halfDayLimitedCount: number;
  /** Get the count for a specific status type, defaults to 0 */
  getTypeCount: (status: PetStatusNotification) => number;
}

/**
 * Get the current ramp context for a user.
 * Single source of truth for counter state calculations.
 *
 * Reset logic (on-demand, not scheduled):
 * - New day → reset ALL counters
 * - New half-day → reset only halfDayLimitedCount
 */
export function getRampContext(state: State, now: number): RampContext {
  // User segment: A (D0-D1), B (D2-D6), C (D7+)
  const daysSinceInstall = getDaysSinceInstall(state.createdAt, now);
  const segment = getStatusMessageSegment(daysSinceInstall);
  const config = statusMessageRampConfigs[segment];

  // Current JST period timestamps
  const currentJstDay = getJSTDayNumber(now);
  const currentHalfDayNumber = getJSTHalfDayNumber(now);
  const rampState = state.chatbot.statusMessageRamp;

  // Detect period changes
  const isNewDay = !rampState || rampState.jstDay !== currentJstDay;
  const isNewHalfDay =
    isNewDay || rampState?.halfDayNumber !== currentHalfDayNumber;

  // Effective counters (0 if reset needed)
  const totalCount = isNewDay ? 0 : rampState.totalCount;
  const halfDayLimitedCount = isNewHalfDay ? 0 : rampState.halfDayLimitedCount;
  const typeCounts = isNewDay ? {} : (rampState.limitedTypeCounts ?? {});

  return {
    projectDaysSinceInstall: daysSinceInstall,
    segment,
    config,
    currentJstDay,
    currentHalfDayNumber,
    isNewDay,
    isNewHalfDay,
    totalCount,
    halfDayLimitedCount,
    getTypeCount: (status: PetStatusNotification) => typeCounts[status] ?? 0,
  };
}

/**
 * Check if a status notification should be sent under the ramp rules.
 * This is called AFTER the existing canSendPetStatusNotification check.
 *
 * @param state - The current state
 * @param status - The pet status triggering the notification
 * @param now - The current timestamp
 * @returns RampFilterResult indicating whether to send and how to update counters
 */
export function shouldSendStatusMessageUnderRamp(
  state: State,
  status: PetStatusNotification,
  now: number,
): RampFilterResult {
  // If not in enabled bucket, allow all messages (control behavior)
  if (!features(state).statusMessageRamp) {
    return {
      shouldSend: true,
      reason: 'control_bucket',
      incrementTotal: false,
      incrementLimited: false,
      status,
    };
  }

  const ctx = getRampContext(state, now);
  const typeCount = ctx.getTypeCount(status);

  // Check if status is suppressed
  if (ctx.config.suppress.includes(status)) {
    return {
      shouldSend: false,
      reason: 'suppressed',
      incrementTotal: false,
      incrementLimited: false,
      status,
    };
  }

  // Always send statuses bypass limited cap
  if (ctx.config.alwaysSend.includes(status)) {
    return {
      shouldSend: true,
      reason: 'always_send',
      incrementTotal: true,
      incrementLimited: false,
      status,
    };
  }

  // Check if daily cap already reached
  if (ctx.totalCount >= ctx.config.maxMessagesPerDay) {
    return {
      shouldSend: false,
      reason: 'daily_cap_reached',
      incrementTotal: false,
      incrementLimited: false,
      status,
    };
  }

  // Limited statuses check their half-day cap and per-type cap
  if (ctx.config.limited.includes(status)) {
    // Check half-day limited cap
    if (ctx.halfDayLimitedCount >= ctx.config.maxLimitedPerHalfDay) {
      return {
        shouldSend: false,
        reason: 'half_day_cap_reached',
        incrementTotal: false,
        incrementLimited: false,
        status,
      };
    }

    // Check per-type limit (daily)
    if (typeCount >= ctx.config.maxLimitedPerType) {
      return {
        shouldSend: false,
        reason: 'per_type_cap_reached',
        incrementTotal: false,
        incrementLimited: false,
        status,
      };
    }

    return {
      shouldSend: true,
      reason: 'limited_allowed',
      incrementTotal: true,
      incrementLimited: true,
      status,
    };
  }

  // Status not in any category - treat as suppressed (safety fallback)
  return {
    shouldSend: false,
    reason: 'suppressed',
    incrementTotal: false,
    incrementLimited: false,
    status,
  };
}

/**
 * Increment status message ramp counters after a message is actually sent.
 * Call this ONLY after deliverChatbotMessage returns true.
 */
export function incrementStatusMessageRampCounters(
  state: MutableState,
  now: number,
  incrementTotal: boolean,
  incrementLimited: boolean,
  status: PetStatusNotification,
): void {
  if (!incrementTotal && !incrementLimited) return;

  const ctx = getRampContext(state, now);

  // New day: reset everything
  if (ctx.isNewDay) {
    state.chatbot.statusMessageRamp = {
      jstDay: ctx.currentJstDay,
      halfDayNumber: ctx.currentHalfDayNumber,
      totalCount: 0,
      halfDayLimitedCount: 0,
      limitedTypeCounts: {},
    };
  }

  const counters = state.chatbot.statusMessageRamp;
  if (!counters) return;

  // New half-day (same day): reset only half-day counter
  if (!ctx.isNewDay && ctx.isNewHalfDay) {
    counters.halfDayNumber = ctx.currentHalfDayNumber;
    counters.halfDayLimitedCount = 0;
  }

  // Increment counters
  if (incrementTotal) counters.totalCount += 1;
  if (incrementLimited) {
    counters.halfDayLimitedCount += 1;
    counters.limitedTypeCounts[status] = ctx.getTypeCount(status) + 1;
  }
}

type StatusMessageSuppressedReason =
  | 'suppressed'
  | 'daily_cap_reached'
  | 'half_day_cap_reached'
  | 'per_type_cap_reached';

function getStatusMessageSuppressedReason(
  rampResult: RampFilterResult,
): StatusMessageSuppressedReason | null {
  switch (rampResult.reason) {
    case 'suppressed':
      return 'suppressed';
    case 'daily_cap_reached':
      return 'daily_cap_reached';
    case 'half_day_cap_reached':
      return 'half_day_cap_reached';
    case 'per_type_cap_reached':
      return 'per_type_cap_reached';
    default:
      return null;
  }
}

export function sendStatusMessageSuppressedAnalytics(
  rampResult: RampFilterResult,
  api: ScheduledActionAPI | SyncActionAPI,
): void {
  const reason = getStatusMessageSuppressedReason(rampResult);
  const eventProperties: {
    feature: 'status';
    subFeature: PetStatusNotification;
    reason?: StatusMessageSuppressedReason;
  } = {
    feature: 'status',
    subFeature: rampResult.status,
  };

  if (reason) {
    eventProperties.reason = reason;
  }

  sendAnalyticsEvents(api, [
    {
      eventType: 'ChatbotMessageSuppressed',
      eventProperties: {
        ...eventProperties,
      },
    },
  ]);
}
